// Intercom Service for the Portal
// Using credentials from zippe_driver app

const INTERCOM_APP_ID = 'xsq0crvx';
const INTERCOM_API_BASE = 'https://api-iam.eu.intercom.io';

class IntercomService {
  constructor() {
    this.isInitialized = false;
    this.currentUser = null;
  }

  // Initialize Intercom script
  initialize() {
    if (this.isInitialized) {
      console.log('[Intercom] Already initialized');
      return;
    }

    // Set up Intercom settings
    window.intercomSettings = {
      api_base: INTERCOM_API_BASE,
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: false, // Show launcher for operators
      alignment: 'right',
      horizontal_padding: 20,
      vertical_padding: 20
    };

    // Load Intercom script
    (function(){
      var w=window;
      var ic=w.Intercom;
      if(typeof ic==="function"){
        ic('reattach_activator');
        ic('update',w.intercomSettings);
      }else{
        var d=document;
        var i=function(){
          i.c(arguments);
        };
        i.q=[];
        i.c=function(args){
          i.q.push(args);
        };
        w.Intercom=i;
        var l=function(){
          var s=d.createElement('script');
          s.type='text/javascript';
          s.async=true;
          s.src='https://widget.intercom.io/widget/' + INTERCOM_APP_ID;
          var x=d.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s,x);
        };
        if(document.readyState==='complete'){
          l();
        }else if(w.attachEvent){
          w.attachEvent('onload',l);
        }else{
          w.addEventListener('load',l,false);
        }
      }
    })();

    this.isInitialized = true;
    console.log('[Intercom] Service initialized');
  }

  // Boot Intercom for a specific user
  boot(user, staffProfile) {
    // IMPORTANT: We don't want to initialize the Intercom widget for operators
    // Operators should use the Intercom Inbox interface instead
    console.log('[Intercom] Operator logged in:', user.email);
    console.log('[Intercom] Operators should use /customer-support to access Intercom Inbox');
    
    // Store current user for tracking purposes
    this.currentUser = user;
    
    // Don't initialize the customer widget for operators
    // They will access Intercom through the dedicated inbox interface
    return;
  }

  // Update user data
  update(updates = {}) {
    if (!this.currentUser) {
      console.warn('[Intercom] No user logged in');
      return;
    }

    console.log('[Intercom] Updating user data:', updates);
    window.Intercom('update', updates);
  }

  // Show the messenger
  show() {
    console.log('[Intercom] Showing messenger');
    window.Intercom('show');
  }

  // Hide the messenger
  hide() {
    console.log('[Intercom] Hiding messenger');
    window.Intercom('hide');
  }

  // Show new message composer
  showNewMessage(preMessage) {
    console.log('[Intercom] Opening new message');
    if (preMessage) {
      window.Intercom('showNewMessage', preMessage);
    } else {
      window.Intercom('showNewMessage');
    }
  }

  // Track custom events
  trackEvent(eventName, metadata = {}) {
    if (!this.currentUser) {
      console.warn('[Intercom] No user logged in, cannot track event');
      return;
    }

    console.log('[Intercom] Tracking event:', eventName, metadata);
    window.Intercom('trackEvent', eventName, metadata);
  }

  // Common events for operators
  trackOperatorAction(action, details = {}) {
    this.trackEvent(`operator_${action}`, {
      ...details,
      timestamp: new Date().toISOString(),
      operator_id: this.currentUser?.uid
    });
  }

  // Shutdown Intercom (on logout)
  shutdown() {
    console.log('[Intercom] Shutting down');
    window.Intercom('shutdown');
    this.currentUser = null;
  }

  // Get unread message count
  getUnreadCount() {
    return new Promise((resolve) => {
      window.Intercom('onUnreadCountChanged', function(unreadCount) {
        resolve(unreadCount);
      });
      // Trigger the callback by updating
      window.Intercom('update');
    });
  }

  // Set up event listeners
  setupEventListeners() {
    // When messenger is opened
    window.Intercom('onShow', function() {
      console.log('[Intercom] Messenger opened');
    });

    // When messenger is closed
    window.Intercom('onHide', function() {
      console.log('[Intercom] Messenger closed');
    });

    // When unread count changes
    window.Intercom('onUnreadCountChanged', function(unreadCount) {
      console.log('[Intercom] Unread count:', unreadCount);
      // You can dispatch this to your state management if needed
      window.dispatchEvent(new CustomEvent('intercom:unreadCountChanged', {
        detail: { count: unreadCount }
      }));
    });
  }
}

// Create singleton instance
const intercomService = new IntercomService();

// Export both the service instance and the class
export default intercomService;
export { IntercomService, INTERCOM_APP_ID };